"use client";

import Image from "next/image";
import Link from "next/link";
import { ChevronRight } from "lucide-react";
import FadeIn from "../animations/FadeIn";
import CardCarousel from "./CardCarousel";

export interface TreatmentItem {
    id: string | number;
    link: string;
    image: string;
    title: string;
}

type TreatmentsDict = {
    home?: { treatments_title?: string; see_details?: string };
    header?: { services?: string };
};

export default function TreatmentsCarousel({
    treatments,
    title,
    description,
    dict,
}: {
    treatments: TreatmentItem[];
    title?: string;
    description?: string;
    dict?: TreatmentsDict;
}) {
    const card = (item: TreatmentItem) => (
        <Link href={item.link} className="group relative flex h-[340px] sm:h-[380px] lg:h-[360px] rounded-[20px] overflow-hidden shadow-[0_12px_30px_rgba(5,30,60,0.12)] hover:shadow-[0_20px_45px_rgba(5,30,60,0.22)] ring-1 ring-black/5 transition-all duration-500 hover:-translate-y-1.5 bg-white">
            <Image
                src={item.image}
                alt={item.title}
                fill
                quality={75}
                sizes="(max-width: 640px) 80vw, (max-width: 1024px) 46vw, 25vw"
                className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
            />
            {/* Gradient Overlay */}
            <div className="absolute inset-0 bg-gradient-to-t from-[#051e3c] via-[#0f4c81]/35 to-transparent opacity-90 group-hover:opacity-95 transition-opacity"></div>

            <div className="absolute inset-x-0 bottom-0 p-5 sm:p-6">
                <h3 className="text-white font-bold text-lg sm:text-xl leading-tight tracking-tight">{item.title}</h3>
                <span className="mt-2 inline-flex items-center text-white/85 text-sm font-medium opacity-0 -translate-y-1 group-hover:opacity-100 group-hover:translate-y-0 transition-all duration-300">
                    {dict?.home?.see_details} <ChevronRight className="w-3.5 h-3.5 ml-1" />
                </span>
            </div>
        </Link>
    );

    return (
        <section className="py-16 sm:py-20 bg-gradient-to-b from-gray-50 to-[#F0F7FF] relative overflow-hidden">
            {/* Background Decor */}
            <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-blue-100/30 rounded-full blur-[100px] -translate-y-1/2 translate-x-1/2 pointer-events-none"></div>

            <div className="container-custom relative z-10">
                <FadeIn>
                    <div className="max-w-5xl mb-8 md:mb-12">
                        <h2 className="text-3xl md:text-4xl font-bold text-[#0f4c81] mb-4 tracking-tight drop-shadow-sm">{title || dict?.home?.treatments_title || dict?.header?.services || "Treatments"}</h2>
                        {description && (
                            <p className="text-gray-600 text-[16px]">
                                {description}
                            </p>
                        )}
                    </div>
                </FadeIn>

                {treatments.length === 0 ? (
                    <div className="py-10 text-center text-gray-600">
                        No active services found.
                    </div>
                ) : (
                    <>
                        {/* Desktop: grid */}
                        <div className="hidden lg:grid grid-cols-4 gap-6">
                            {treatments.map((item, index) => (
                                <FadeIn key={item.id} delay={index * 80} className="h-full">
                                    {card(item)}
                                </FadeIn>
                            ))}
                        </div>

                        {/* Mobile / tablet: carousel */}
                        <div className="lg:hidden">
                            <CardCarousel
                                ariaLabel="treatment"
                                itemClassName="w-[80%] sm:w-[46%] md:w-[38%]"
                                items={treatments.map((item) => card(item))}
                            />
                        </div>
                    </>
                )}
            </div>
        </section>
    );
}
